import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";
import toast from "react-hot-toast";
import { getSingleNeighborhoodNews, updateNeiborhoodNews } from "../../redux/ApiSlice/Neighborhood/NeighborhoodNewsSlice";
import { MessageCircle } from "lucide-react";
import { IoMdArrowBack } from "react-icons/io";

const UpdateNews = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const loading = useSelector((state) => state.NeighborhoodNews.loading);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        allow_responses: "no"
    });
    const [isLoading, setIsLoading] = useState(false);

    const fetchNews = useCallback(async () => {
        try {
            const response = await dispatch(getSingleNeighborhoodNews({ id })).unwrap();
            setFormData({
                title: response.title || "",
                description: response.news_description || "",
                allow_responses: response.allow_responses === "yes" ? "yes" : "no"
            });
        } catch (error) {
            toast.error("Failed to fetch neighborhood news. Please try again later.");
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchNews();
    }, [id, fetchNews]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { title, description } = formData;

        if (!title || !description) {
            toast.error("All fields are required.");
            return;
        }

        const submitData = new FormData();
        submitData.append("title", title);
        submitData.append("news_description", description);
        submitData.append("allow_responses", formData.allow_responses);

        try {
            setIsLoading(true);
            await dispatch(updateNeiborhoodNews({ newsId: id, formData: submitData })).unwrap();
            toast.success("News updated successfully!");
            setTimeout(() => navigate(-1), 1500);
        } catch (error) {
            toast.error("Failed to update news.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white dark:bg-[#1E1D21] h-full overflow-y-scroll">
            <div className="sticky top-0 bg-white dark:bg-[#1E1D21]">
                <div className="h-12 flex items-center justify-center gap-x-4 bg-[#BDE6FF] shadow-sm rounded-b-xl relative">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="absolute left-2 text-[24px] cursor-pointer"
                    />
                    <p className="font-bold text-[18px]">Update Your Post</p>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center mt-10">
                    <ClipLoader size={30} color={"#000"} />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="max-w-lg mx-auto mt-1 px-4 mb-5">
                    <div className="mb-3">
                        <label htmlFor="title" className="block text-[14px] font-medium dark:text-gray-300 text-black">
                            News Title
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            placeholder="This is what will show on the news"
                            value={formData.title}
                            onChange={handleChange}
                            className="mt-1 block w-full text-[13px] border border-gray-300 dark:border-gray-800/50 rounded-md shadow-sm p-2 focus:ring-1 dark:bg-[#333333] dark:text-gray-200 focus:ring-cyan-500 outline-none"
                        />
                    </div>

                    <div className="mb-3">
                        <label htmlFor="description" className="block text-[14px] font-medium dark:text-gray-300 text-black">
                            Description
                        </label>
                        <textarea
                            rows={5}
                            id="description"
                            name="description"
                            placeholder="Write a description..."
                            value={formData.description}
                            onChange={handleChange}
                            className="mt-1 block w-full text-[13px] border border-gray-300 dark:border-gray-800/50 rounded-md shadow-sm p-2 resize-none focus:ring-1 dark:bg-[#333333] dark:text-gray-200 focus:ring-cyan-500 outline-none"
                        />
                    </div>

                    <div className="mb-3">
                        <div className="flex items-center justify-between p-4 bg-white dark:bg-[#333333] border-2 dark:border-gray-800/50 rounded-xl">
                            <div className="flex items-center gap-3">
                                <MessageCircle className="h-5 w-5 text-black dark:text-gray-300" />
                                <span className="text-[14px] font-semibold text-gray-700 dark:text-gray-200">Allow responses</span>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.allow_responses === "yes"}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, allow_responses: e.target.checked ? "yes" : "no" }))
                                    }
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-400"></div>
                            </label>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-black dark:bg-[#333333] dark:text-gray-200 text-white font-semibold py-2 px-4 rounded-md shadow hover:bg-gray-800 focus:outline-none"
                        disabled={isLoading}
                    >
                        {isLoading ? <ClipLoader size={18} color={"#fff"} /> : "Update Post"}
                    </button>
                </form>
            )}
        </div>
    );
};

export default UpdateNews;
